"use client"

import React from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import Header from "../../../components/header"
import Footer from "../../../components/footer"
import { Badge } from "@/components/ui/badge"
import { ArrowLeft } from "lucide-react"
import { useLanguage } from "../../../contexts/language-context"

export default function OrchestratorProtocolPage() {
  const router = useRouter()
  const { language } = useLanguage()
  const isEs = language === 'es'

  // Metadata configuration
  const meta = {
    title: isEs ? "El Protocolo Orquestador: Arquitectura de Sistemas 2026" : "The Orchestrator Protocol: System Architecture for 2026",
    date: "2026-02-05",
    readTime: isEs ? "8 min lectura" : "8 min read",
    tags: isEs 
      ? ["Sistemas", "Arquitectura", "IA", "Estrategia"] 
      : ["Systems", "Architecture", "AI", "Strategy"]
  }

  return (
    <>
      <Header />
      <main className="main-content pb-16 bg-slate-950 text-slate-200">
        
        {/* Navigation / Back */}
        <div className="container mx-auto px-6 pt-24 pb-12">
           <button 
             onClick={() => router.back()}
             className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors mb-8 group font-mono text-sm"
           >
             <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
             {isEs ? "VOLVER AL BLOG" : "BACK TO INTELLIGENCE"}
           </button>

           {/* Header */}
           <header className="max-w-4xl mx-auto text-center mb-12">
             <div className="flex justify-center gap-2 mb-6">
               {meta.tags.map(tag => (
                 <Badge key={tag} variant="outline" className="border-cyan-500/30 text-cyan-400">
                   {tag}
                 </Badge>
               ))}
             </div>
             <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white tracking-tight leading-tight">
               {meta.title}
             </h1>
             <div className="flex justify-center items-center gap-4 text-slate-400 font-mono text-sm">
               <span>{meta.date}</span>
               <span className="text-slate-600">•</span>
               <span>{meta.readTime}</span>
             </div>
           </header>

           {/* Hero Image */}
           <div className="max-w-4xl mx-auto mb-16 relative aspect-[16/9] rounded-2xl overflow-hidden border border-slate-800 shadow-2xl shadow-cyan-900/10">
             <Image 
               src="/images/blog/featured/orchestrator-protocol-system-architecture-en.jpg" 
               alt="Orchestrator System Architecture"
               fill
               className="object-cover"
               priority
             />
           </div>

           {/* CONTENT BODY */}
           <article className="max-w-3xl mx-auto prose prose-invert prose-lg prose-cyan">
             
             {/* ENGLISH CONTENT */}
             {!isEs && (
               <>
                 <p className="lead text-2xl text-slate-300 font-light leading-relaxed">
                   The "Solopreneur" model is dead. It is now recognized as a single point of failure—a "smaller cage" built on the flawed logic of Bottleneck Math.
                 </p>
                 
                 <p>
                   The professional landscape for independent experts has undergone a seismic shift. 
                   We have moved from the linear accumulation of tasks to the <strong>architectural management of specialized intelligence</strong>. 
                   In 2026, the competitive advantage is no longer "working hard." It is architectural leverage.
                 </p>

                 <h3 className="text-white mt-12">The Physics of Bottleneck Math</h3>
                 <p>
                   The fundamental flaw of the traditional solo model is rooted in the physics of effort. 
                   If you stop typing, revenue stops. If you stop editing, the project stalls. 
                   This linear relationship creates a ceiling on growth, turning your own time into the ultimate constraint.
                 </p>
                 <p>
                   The Orchestrator Protocol is the final divorce from labor-intensive delivery. 
                   It is about transforming from a "Worker" into a <strong>Chief of Staff</strong>.
                 </p>

                 <h3 className="text-white mt-12">The Solution: Synthetic Departments</h3>
                 <p>
                   Instead of chatting with a generic AI bot, the Orchestrator builds "Synthetic Departments"—dedicated, 
                   persistent systems with narrow contexts. This is the exact stack running this studio:
                 </p>
                 <ul className="list-none space-y-4 pl-0 my-8">
                   <li className="bg-slate-900/50 p-6 rounded-lg border-l-2 border-cyan-500">
                     <strong className="text-cyan-400 block mb-1">Gem A // Retail Architect</strong>
                     Handles Three.js execution, mobile performance optimization, and e-commerce logic.
                   </li>
                   <li className="bg-slate-900/50 p-6 rounded-lg border-l-2 border-purple-500">
                     <strong className="text-purple-400 block mb-1">Gem B // Systems Architect</strong>
                     Manages database integrity, API security, and lead capture funnels.
                   </li>
                   <li className="bg-slate-900/50 p-6 rounded-lg border-l-2 border-emerald-500">
                     <strong className="text-emerald-400 block mb-1">Gem D // Brand Publisher</strong>
                     Executes distribution strategies and content production.
                   </li>
                 </ul>

                 <h3 className="text-white mt-12">Protecting the Creative Sovereign</h3>
                 <p>
                   This system isn't just about productivity; it's about sanity. By delegating the execution to 
                   specialized agents, I reclaim the mental space needed for high-stakes negotiation and strategic design.
                 </p>
                 <p>
                   <strong>Stop building a job for yourself. Start building a machine.</strong>
                 </p>
               </>
             )}

             {/* SPANISH CONTENT */}
             {isEs && (
               <>
                 <p className="lead text-2xl text-slate-300 font-light leading-relaxed">
                   El modelo del "Solopreneur" ha muerto. Ahora se reconoce como un punto único de fallo: una "jaula más pequeña" construida sobre la lógica defectuosa de las Matemáticas del Cuello de Botella.
                 </p>
                 
                 <p>
                   El panorama profesional para expertos independientes ha sufrido un cambio sísmico. 
                   Hemos pasado de la acumulación lineal de tareas a la <strong>gestión arquitectónica de inteligencia especializada</strong>. 
                   En 2026, la ventaja competitiva ya no es "trabajar duro". Es el apalancamiento arquitectónico.
                 </p>

                 <h3 className="text-white mt-12">La Física del Cuello de Botella</h3>
                 <p>
                   El defecto fundamental del modelo tradicional radica en la física del esfuerzo. 
                   Si dejas de escribir, los ingresos se detienen. Si dejas de editar, el proyecto se estanca. 
                   Esta relación lineal crea un techo en el crecimiento, convirtiendo tu propio tiempo en la restricción definitiva.
                 </p>
                 <p>
                   El Protocolo Orquestador es el divorcio final de la entrega intensiva en mano de obra. 
                   Se trata de transformarse de un "Trabajador" en un <strong>Jefe de Estado Mayor</strong>.
                 </p>

                 <h3 className="text-white mt-12">La Solución: Departamentos Sintéticos</h3>
                 <p>
                   En lugar de chatear con un bot de IA genérico, el Orquestador construye "Departamentos Sintéticos": sistemas dedicados y persistentes con contextos estrechos. Esta es la pila exacta que ejecuta este estudio:
                 </p>
                 <ul className="list-none space-y-4 pl-0 my-8">
                   <li className="bg-slate-900/50 p-6 rounded-lg border-l-2 border-cyan-500">
                     <strong className="text-cyan-400 block mb-1">Gema A // Arquitecto Retail</strong>
                     Maneja la ejecución de Three.js, optimización móvil y lógica de comercio electrónico.
                   </li>
                   <li className="bg-slate-900/50 p-6 rounded-lg border-l-2 border-purple-500">
                     <strong className="text-purple-400 block mb-1">Gema B // Arquitecto de Sistemas</strong>
                     Gestiona la integridad de la base de datos, seguridad de API y embudos de captura.
                   </li>
                   <li className="bg-slate-900/50 p-6 rounded-lg border-l-2 border-emerald-500">
                     <strong className="text-emerald-400 block mb-1">Gema D // Editor de Marca</strong>
                     Ejecuta estrategias de distribución y producción de contenido.
                   </li>
                 </ul>

                 <h3 className="text-white mt-12">Protegiendo al Soberano Creativo</h3>
                 <p>
                   Este sistema no es solo sobre productividad; es sobre cordura. Al delegar la ejecución a 
                   agentes especializados, recupero el espacio mental necesario para negociaciones de alto riesgo y diseño estratégico.
                 </p>
                 <p>
                   <strong>Deja de construir un trabajo para ti mismo. Empieza a construir una máquina.</strong>
                 </p>
               </>
             )}

             <div className="mt-16 pt-8 border-t border-slate-800 text-center">
               <p className="text-slate-400 italic">
                 {isEs 
                   ? "¿Quieres auditar tu propia arquitectura? Envíame un DM en LinkedIn."
                   : "Want to audit your own architecture? DM me on LinkedIn."
                 }
               </p>
             </div>

           </article>
        </div>
      </main>
      <Footer />
    </>
  )
}
